<template>
  <section class="graph-panel">
    <div class="graph-toolbar">
      <h2 class="panel-title">3D 场景</h2>
      <div class="toolbar-actions">
        <button class="action-btn" :disabled="!graph || locked" @click="openNodeDialog()">
          + 新增节点
        </button>
        <button
          class="action-btn"
          :class="{ active: edgeMode }"
          :disabled="!graph || locked"
          @click="toggleEdgeMode"
        >
          {{ edgeMode ? '✓ 连边中…' : '连边模式' }}
        </button>
        <button class="action-btn" :disabled="!graph || locked" @click="fitView">
          自适应
        </button>
        <button class="action-btn" :disabled="!graph || locked" title="先选择节点；未选择时查看第一个节点" @click="focusSelected">
          设备特写
        </button>
        <button class="action-btn primary" :disabled="!graph || locked" @click="saveAsImage">
          保存为图片
        </button>
      </div>
    </div>
    <div v-if="nodeDialog" class="name-dialog-backdrop" @click.self="nodeDialog = null">
      <form class="name-dialog" role="dialog" aria-modal="true" aria-labelledby="node-dialog-title" @submit.prevent="confirmNodeName">
        <h3 id="node-dialog-title">{{ nodeDialog.id ? '编辑节点名称' : '新增节点' }}</h3>
        <label for="node-name">节点名称</label>
        <input id="node-name" ref="nodeNameInput" v-model="nodeName" autocomplete="off" @keydown.esc="nodeDialog = null" />
        <div class="toolbar-actions">
          <button type="button" class="action-btn" @click="nodeDialog = null">取消</button>
          <button type="submit" class="action-btn primary">确定</button>
        </div>
      </form>
    </div>
    <div ref="container" class="graph-container" :class="{locked}">
      <div v-if="hover" class="graph-tooltip" :style="{ left: Math.max(8, Math.min(hover.x + 14, $refs.container.clientWidth - 250)) + 'px', top: Math.max(8, Math.min(hover.y + 14, $refs.container.clientHeight - 230)) + 'px' }">
        <strong>{{ hover.model.label || hover.model.linkId || hover.model.id }}</strong>
        <div>状态：{{ statusLabel(hover.model.status) }}</div>
        <div v-if="hover.model.category">型号：{{ modelLabel(hover.model) }}</div>
        <div v-if="hover.model.linkType">{{ hover.model.linkType === 'wireless' ? '无线链路' : '有线链路' }}</div>
        <div v-for="(value, key) in hover.model.meta" :key="key">{{ key }}：{{ value }}</div>
        <template v-for="side in ['sourceNic', 'targetNic']" :key="side">
          <div v-if="hover.model[side]" class="nic-tooltip">
            {{ side === 'sourceNic' ? '源接口' : '目标接口' }}：{{ hover.model[side].id }} · {{ statusLabel(hover.model[side].status) }}
            <div v-for="(value, key) in { ...hover.model[side].meta, ...hover.model[side].props }" :key="key">{{ key }}：{{ value }}</div>
          </div>
        </template>
      </div>
      <div v-if="!graph" class="placeholder">
        {{ renderError || '请在左侧输入并点击“生成场景”' }}
      </div>
      <div v-if="graph" class="status-legend">
        <span v-for="(style, status) in statusStyles" :key="status">
          <i :style="{ background: style.color }"></i>{{ style.label }}
        </span>
      </div>
      <div v-if="graph" class="hint">
        {{ edgeMode ? '连边：依次点击两个节点，或从源节点拖到目标节点' : '拖动空白旋转 · 滚轮缩放 · 右键平移 · 拖动节点 · 双击改名 · Delete 删除' }}
      </div>
    </div>
  </section>
</template>

<script>
import { markRaw } from 'vue'
import ThreeTopology from '../graph/ThreeTopology'
import { STATUS_STYLES, styleForCategory } from '../api/topology'
import { getNodeModelOptions, getNodeModelSpec } from '../graph/modelLibrary'

// 按分类 + 状态计算节点样式：分类决定形状与底色，异常状态覆盖描边。
function nodeStyle(category, status) {
  const visual = styleForCategory(category)
  const style = { ...visual.style }
  const s = STATUS_STYLES[status]
  if (s && status && status !== 'normal') {
    style.stroke = s.color
    style.lineWidth = 3
  }
  return { type: visual.type, style }
}

// 按状态 + 类型 + 容量计算边样式。
function edgeStyle(status, linkType, value) {
  const s = STATUS_STYLES[status] || STATUS_STYLES.normal
  return {
    stroke: s.color,
    lineWidth: edgeWidth(value),
    lineDash: linkType === 'wireless' ? [6, 4] : undefined,
    endArrow: false
  }
}

// 边宽根据容量/吞吐（value, 单位约等于 Mbps）线性映射，凸显链路粗细
function edgeWidth(value) {
  if (!value) return 1.5
  return Math.max(1.5, Math.min(6, value / 180))
}

export default {
  name: 'TopologyGraph',
  props: {
    data: { type: Object, default: null },
    locked: Boolean
  },
  emits: ['select'],
  data() {
    return { statusStyles: STATUS_STYLES, graph: null, edgeMode: false, hover: null, renderError: '', nodeDialog: null, nodeName: '' }
  },
  mounted() {
    window.addEventListener('resize', this.handleResize)
    window.addEventListener('keydown', this.handleKeydown)
    if (this.data) this.renderGraph(this.data)
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.handleResize)
    window.removeEventListener('keydown', this.handleKeydown)
    this.destroyGraph()
  },
  watch: {
    locked(value) {if(value){this.nodeDialog=null;this.edgeMode=false;this.graph?.setMode('default')}},
    data(newData) {
      if (newData) this.renderGraph(newData)
      else this.destroyGraph()
    }
  },
  methods: {
    statusLabel(status) { return (STATUS_STYLES[status] || STATUS_STYLES.normal).label },
    modelLabel(model) {
      return getNodeModelSpec(model.category, model.modelId, model.id)?.name || '默认图形'
    },
    destroyGraph() {
      if (this.graph) {
        this.graph.destroy()
        this.graph = null
      }
      this.edgeMode = false
      this.hover = null
      this.nodeDialog = null
      this.$emit('select', null)
    },
    renderGraph(data) {
      this.destroyGraph()
      this.renderError = ''
      let graph
      try {
        graph = markRaw(new ThreeTopology({
          container: this.$refs.container,
          onHover: (hover) => { this.hover = hover }
        }))
        this.graph = graph
        graph.data(data)
        graph.render()
        graph.on('node:dblclick', this.handleNodeDblClick)
        graph.on('aftercreateedge', ({ edge }) => {
          graph.setMode('default')
          this.edgeMode = false
          this.selectItem('edge', edge)
        })
        graph.on('node:click', ({ item }) => this.emitSelect('node', item))
        graph.on('edge:click', ({ item }) => this.emitSelect('edge', item))
        graph.on('canvas:click', () => this.$emit('select', null))
      } catch (error) {
        if (graph) graph.destroy()
        this.graph = null
        this.renderError = '无法启动 3D 视图，请检查浏览器是否支持 WebGL 2 并开启硬件加速。'
        console.error('Three.js topology initialization failed', error)
      }
    },
    emitSelect(itemType, item) {
      const model = item.getModel()
      // g6Id 沿用旧接口字段名，表示内部实体 id，用于后续更新/删除；对边，业务 id 仍优先用 linkId。
      this.$emit('select', {
        itemType,
        g6Id: model.id,
        id: itemType === 'edge' ? model.linkId || '' : model.id,
        label: model.label || (itemType === 'node' ? model.id : ''),
        category: model.category || '',
        modelId: model.modelId || '',
        status: model.status || 'normal',
        linkType: model.linkType || '',
        linkId: model.linkId || '',
        value: typeof model.value === 'number' ? model.value : undefined,
        source: typeof model.source === 'string' ? model.source : '',
        target: typeof model.target === 'string' ? model.target : '',
        meta: model.meta || {},
        sourceNic: model.sourceNic || null,
        targetNic: model.targetNic || null
      })
    },
    // 把详情面板提交的编辑写回图，并重算样式后返回最新快照。
    applyItemUpdate(payload) {
      if (!this.graph || this.graph.get('destroyed')) return null
      const { itemType, g6Id, model } = payload
      const item = this.graph.findById(g6Id)
      if (!item) return null
      const update = {}
      if ('label' in model) update.label = model.label
      if ('status' in model) update.status = model.status
      if ('meta' in model) update.meta = model.meta
      if (itemType === 'node') {
        if ('category' in model) update.category = model.category
        if ('modelId' in model) update.modelId = model.modelId
        const visual = nodeStyle(
          'category' in model ? model.category : item.getModel().category,
          'status' in model ? model.status : item.getModel().status
        )
        update.type = visual.type
        update.style = visual.style
      } else {
        if ('linkId' in model) update.linkId = model.linkId
        if ('linkType' in model) update.linkType = model.linkType
        if ('value' in model) update.value = model.value
        // 网卡编辑写回；null 表示清空该端网卡（透传原值，替换而非合并）。
        if ('sourceNic' in model) update.sourceNic = model.sourceNic || null
        if ('targetNic' in model) update.targetNic = model.targetNic || null
        const current = item.getModel()
        update.style = edgeStyle(
          'status' in model ? model.status : current.status,
          'linkType' in model ? model.linkType : current.linkType,
          'value' in model ? model.value : current.value
        )
      }
      this.graph.updateItem(item, update)
      return {
        itemType,
        g6Id,
        id:
          itemType === 'edge'
            ? item.getModel().linkId || ''
            : item.getModel().id,
        label: item.getModel().label || '',
        category: item.getModel().category || '',
        modelId: item.getModel().modelId || '',
        status: item.getModel().status || 'normal',
        linkType: item.getModel().linkType || '',
        linkId: item.getModel().linkId || '',
        value:
          typeof item.getModel().value === 'number'
            ? item.getModel().value
            : undefined,
        source: item.getModel().source || '',
        target: item.getModel().target || '',
        meta: item.getModel().meta || {},
        sourceNic: item.getModel().sourceNic || null,
        targetNic: item.getModel().targetNic || null
      }
    },
    removeItemById(g6Id) {
      if (!this.graph || this.graph.get('destroyed')) return
      const item = this.graph.findById(g6Id)
      if (item) this.graph.removeItem(item)
    },
    // 按后端演化结果批量更新实体状态：states = [{ id, status }]。
    // id 可能是节点（N…，即节点 id）或信道（C…，对应边的 linkId）；
    // 分别重算节点/边样式并重绘。返回实际命中的实体数，供上层提示。
    applyEntityStates(states) {
      if (!this.graph || this.graph.get('destroyed')) return 0
      if (!Array.isArray(states)) return 0
      let applied = 0
      states.forEach((state) => {
        const { id, status } = state || {}
        if (!id || !Object.prototype.hasOwnProperty.call(STATUS_STYLES, status)) return
        const key = String(id)
        // 网卡（N…:IF…）：定位到承载它的边，给对应端 NIC 上色后重绘标记。
        if (key.includes(':')) {
          if (this._applyNicState(key, status)) applied += 1
          return
        }
        // 先按节点定位（节点业务 id == 内部 id）。
        const node = this.graph.findById(key)
        if (node && node.getType() === 'node') {
          this.graph.updateNodeStatus(node, status)
          applied += 1
          return
        }
        // 否则按信道 id 定位边（边的业务 id 存在 linkId）。
        const edge = this.graph
          .getEdges()
          .find((e) => String(e.getModel().linkId || e.getModel().id) === key)
        if (edge) {
          const m = edge.getModel()
          this.graph.updateItem(edge, {
            status,
            style: edgeStyle(status, m.linkType, m.value)
          })
          applied += 1
        }
      })
      const selected = this.graph.findAllByState('node', 'selected')[0] ||
        this.graph.findAllByState('edge', 'selected')[0]
      if (selected) this.emitSelect(selected.getType(), selected)
      return applied
    },
    // 给某个网卡上色：找到 sourceNic/targetNic.id 匹配的边，写入该端 NIC 的
    // status，并重画所有承载该网卡的链路标记。
    _applyNicState(nicId, status) {
      const edges = this.graph.getEdges().filter((e) => {
        const m = e.getModel()
        return [m.sourceNic, m.targetNic].some((nic) => nic && String(nic.id) === nicId)
      })
      edges.forEach((edge) => {
        const m = edge.getModel()
        const patch = {}
        ;['sourceNic', 'targetNic'].forEach((side) => {
          if (m[side] && String(m[side].id) === nicId) patch[side] = { ...m[side], status }
        })
        this.graph.updateItem(edge, patch)
      })
      return edges.length > 0
    },
    // 选中并居中一批实体（节点 N… 或信道 C…），用于演化后高亮变化的实体。
    focusEntities(ids) {
      if (!this.graph || this.graph.get('destroyed')) return
      const items = []
      ;(ids || []).forEach((id) => {
        const key = String(id)
        let item = this.graph.findById(key)
        if (!item || item.getType() !== 'node') {
          // 信道按 linkId、网卡（含 :）按承载它的边定位。
          item = this.graph.getEdges().find((e) => {
            const m = e.getModel()
            if (key.includes(':')) {
              return (
                (m.sourceNic && String(m.sourceNic.id) === key) ||
                (m.targetNic && String(m.targetNic.id) === key)
              )
            }
            return String(m.linkId || m.id) === key
          })
        }
        if (item) items.push(item)
      })
      if (!items.length) return
      // 清除旧选中，再选中变化的实体。
      this.graph
        .findAllByState('node', 'selected')
        .forEach((it) => this.graph.setItemState(it, 'selected', false))
      this.graph
        .findAllByState('edge', 'selected')
        .forEach((it) => this.graph.setItemState(it, 'selected', false))
      items.forEach((it) => this.graph.setItemState(it, 'selected', true))
      // 居中到第一个变化的实体，并同步选中态到详情面板。
      this.graph.focusItem(items[0], true, {
        easing: 'easeCubic',
        duration: 400
      })
      this.emitSelect(items[0].getType(), items[0])
    },
    openNodeDialog(item = null) {
      this.nodeDialog = { id: item ? item.getModel().id : null }
      this.nodeName = item ? item.getModel().label || '' : '新节点'
      this.$nextTick(() => { this.$refs.nodeNameInput.focus(); this.$refs.nodeNameInput.select() })
    },
    confirmNodeName() {
      const id = this.nodeDialog.id
      if (id) {
        const item = this.graph.findById(id)
        if (item) {
          this.graph.updateItem(item, { label: this.nodeName.trim() || id })
          this.selectItem('node', item)
        }
      } else this.addNode(this.nodeName.trim())
      this.nodeDialog = null
    },
    handleNodeDblClick(e) { this.openNodeDialog(e.item) },
    handleKeydown(e) {
      if (this.locked || this.$el?.offsetParent === null) return
      if (!this.graph || this.nodeDialog) return
      if (e.key === 'Escape' && this.edgeMode) { this.toggleEdgeMode(); return }
      if (e.key !== 'Delete' && e.key !== 'Backspace') return
      const tag = (e.target.tagName || '').toLowerCase()
      if (tag === 'input' || tag === 'textarea' || tag === 'select' || e.target.isContentEditable) return
      const nodes = this.graph.findAllByState('node', 'selected')
      const edges = this.graph.findAllByState('edge', 'selected')
      if (!nodes.length && !edges.length) return
      e.preventDefault()
      ;[...nodes, ...edges].forEach((item) => this.graph.removeItem(item))
    },
    addNode(label = '新节点') {
      const id = this.graph.newId('node')
      const containerEl = this.graph.getContainer()
      const rect = containerEl.getBoundingClientRect()
      const point = this.graph.getPointByClient(
        rect.left + rect.width / 2,
        rect.top + rect.height / 2
      )
      // 以默认分类/状态创建，并自动选中，随后在右侧详情面板补充参数。
      const category = 'router'
      const visual = nodeStyle(category, 'normal')
      const item = this.graph.addItem('node', {
        id,
        label: label || id,
        category,
        modelId: getNodeModelOptions(category)[0]?.id || '',
        status: 'normal',
        meta: {},
        type: visual.type,
        style: visual.style,
        x: point.x + (Math.random() - 0.5) * 80,
        y: point.y + (Math.random() - 0.5) * 80,
        z: point.z
      })
      this.selectItem('node', item)
    },
    // 选中某个 item（清除其他选中态）并向外发出选择事件。
    selectItem(itemType, item) {
      this.graph.findAllByState('node', 'selected').forEach((it) =>
        this.graph.setItemState(it, 'selected', false)
      )
      this.graph.findAllByState('edge', 'selected').forEach((it) =>
        this.graph.setItemState(it, 'selected', false)
      )
      this.graph.setItemState(item, 'selected', true)
      this.emitSelect(itemType, item)
    },
    toggleEdgeMode() {
      this.edgeMode = !this.edgeMode
      this.graph.setMode(this.edgeMode ? 'addEdge' : 'default')
    },
    handleResize() {
      if (!this.graph || this.graph.get('destroyed')) return
      const container = this.$refs.container
      this.graph.changeSize(container.clientWidth, container.clientHeight)
    },
    fitView() {
      if (this.graph) this.graph.fitView(40)
    },
    focusSelected() {
      if (!this.graph) return
      const item = this.graph.findAllByState('node', 'selected')[0] || this.graph.getNodes()[0]
      if (!item) return
      this.graph.focusItem(item, true)
      this.selectItem('node', item)
    },
    // 导出当前图模型（含用户新增/删除/连边/改名等编辑），供诊断转换使用。
    exportData() {
      if (!this.graph || this.graph.get('destroyed')) return null
      return this.graph.save()
    },
    saveAsImage() {
      if (!this.graph) return
      this.graph.downloadFullImage('topology')
    }
  }
}
</script>

<style scoped>
.graph-container.locked{pointer-events:none;opacity:.85}
.name-dialog-backdrop { position: absolute; inset: 0; z-index: 10; display: flex; align-items: center; justify-content: center; background: #0f172a55; }
.name-dialog { width: 280px; padding: 24px; background: white; border-radius: 12px; box-shadow: 0 20px 60px #0f172a33; }
.name-dialog h3 { margin-top: 0; }
.name-dialog label { display: block; margin-bottom: 8px; font-size: 13px; }
.name-dialog input { box-sizing: border-box; width: 100%; padding: 10px; margin-bottom: 20px; border: 1px solid #cbd5e1; border-radius: 6px; }
.graph-panel {
  position: relative;
  flex: 1;
  display: flex;
  flex-direction: column;
  background: #fafafa;
  min-width: 0;
}
.graph-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 10px;
  padding: 12px 20px;
  border-bottom: 1px solid #eaeaea;
  background: #fff;
}
.panel-title {
  margin: 0;
  font-size: 18px;
  color: #1f1f1f;
}
.toolbar-actions {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}
.action-btn {
  padding: 6px 14px;
  border: 1px solid #d9d9d9;
  background: #fff;
  border-radius: 4px;
  cursor: pointer;
  font-size: 13px;
  transition: all 0.2s;
}
.action-btn:hover:not(:disabled) {
  border-color: #1890ff;
  color: #1890ff;
}
.action-btn.active {
  background: #fff7e6;
  border-color: #fa8c16;
  color: #fa8c16;
}
.action-btn.primary {
  background: #1890ff;
  color: #fff;
  border-color: #1890ff;
}
.action-btn.primary:hover:not(:disabled) {
  background: #40a9ff;
}
.action-btn:disabled {
  cursor: not-allowed;
  opacity: 0.5;
}
.graph-container {
  flex: 1;
  position: relative;
  overflow: hidden;
}
.placeholder {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #bfbfbf;
  font-size: 14px;
  pointer-events: none;
}
.graph-tooltip {
  position: absolute; z-index: 3; width: 220px; max-height: 210px; overflow: hidden;
  padding: 12px; border: 1px solid #dbe3ef; border-radius: 8px; background: rgba(255,255,255,.96);
  box-shadow: 0 8px 30px #0f172a18; font-size: 12px; line-height: 1.7; pointer-events: none; overflow-wrap: anywhere;
}
.nic-tooltip { margin-top: 6px; border-top: 1px solid #e2e8f0; }
.graph-container :deep(canvas) { display: block; touch-action: none; }
.status-legend { position: absolute; top: 12px; left: 16px; z-index: 2; display: flex; gap: 12px; padding: 7px 10px; border-radius: 6px; background: rgba(255,255,255,.9); font-size: 12px; pointer-events: none; }
.status-legend span { display: flex; align-items: center; gap: 5px; }
.status-legend i { width: 8px; height: 8px; border-radius: 50%; }
.hint {
  z-index: 2;
  position: absolute;
  left: 16px;
  bottom: 12px;
  font-size: 12px;
  color: #8c8c8c;
  background: rgba(255, 255, 255, 0.85);
  padding: 4px 10px;
  border-radius: 4px;
  pointer-events: none;
}
</style>


